import moment from 'moment';
import * as dates from '@simplybenefits/sb-dates';
import { constants } from '@simplybenefits/sb-utils';

const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

/**
 * Dates can come in as different objects or formats....
 * @param date
 * @returns {*}
 */
const formatDate = (date) => {
    const dateFormat = 'MMM DD, YYYY';
    return date._isAMomentObject === true ? date.format(dateFormat) : dates.formatDateForDisplay(date, dateFormat);
};

const reportsData = {
    'Members And Usage': {
        'Onboarding': {
            icon: 'personAdd',
            description: 'Pull a report for Onboarding based on Carrier',
            filters: {
                carrier: {
                    type: 'select',
                    label: 'Carrier',
                    options: [ 'Advica', 'AIG', 'Allstate', 'ASO', 'Assumption Life', 'CHUBB', 'Desjardins', 'Empire', 'Enriched Academy', 'Equitable Life', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Orion', 'SSQ', 'Sunlife', 'Wawanesa' ],
                    value: 'Empire'
                },
                userType: {
                    type: 'bubble-select',
                    label: 'User Type',
                    options: [ 'Members', 'Dependents' ],
                    value: 'Members'
                }
            }
        },
        'Optional Benefits': {
            icon: 'star',
            description: 'Pull a Optional Benefits Report',
            filters: {
                month: {
                    type: 'month',
                    value: firstOfMonth
                },
                type: {
                    type: 'select',
                    label: 'Type',
                    options: [
                        'All',
                        'optLife',
                        'optLifeSpouse',
                        'optLifeChild',
                        'optAdd',
                        'optAddSpouse',
                        'optAddChild',
                        'optCi',
                        'optCiSpouse',
                        'optCiChild'
                    ],
                    value: 'All'
                }
            }
        },
        'Over Age Member Listing': {
            title: 'Over-age Member Anniversary',
            icon: 'elderly',
            description: 'Pull a report of members turning a certain age within a daterange',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment(),
                    endDate: moment().add(6, 'month')
                },
                age: {
                    type: 'number',
                    label: 'Age',
                    isRequired: true,
                    value: 65
                }
            }
        },
        'Overage Dependent Listing': {
            title: 'Over-age Dependent',
            icon: 'cake',
            description: 'View all current dependents who are over age.',
            testid: 'over-age-dependents',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        },
        'Dependent Listing': {
            icon: 'groups',
            description: 'View all current dependents.',
            testid: 'dependent-listing',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Active',
                        'Account Disabled'
                    ],
                    value: 'All'
                }
            }
        },
        'LTD': {
            icon: 'dateRange',
            description: 'Review LTD volume changes and associated premium details for members with recent coverage updates.',
            filters: {}
        },
        'Certificate Listing': {
            icon: 'assignment',
            description: 'Get data on each member.',
            testid: 'certificate-listing',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Active',
                        'Account Disabled',
                        'Pending',
                        'Late Applicants',
                        'Terminated'
                    ],
                    value: 'All'
                }
            }
        },
        'Spending Account Usages': {
            title: 'HSA & LSA Usages',
            icon: 'receipt',
            description: 'View spending account premiums.',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' },
                class: { default: 'All Classes' }
            },
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                member: true,
                type: {
                    type: 'select',
                    label: 'Benefit',
                    options: [ 'All', 'HSA', 'LSA' ],
                    value: 'All'
                }
            }
        },
        'HSA Balance': {
            icon: 'healthAndSafety',
            description: 'View the current HSA balances per member.',
            testid: 'hsa-balance',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' },
                class: { default: 'All Classes' }
            },
            filters: {
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions',
                    isRequired: true
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Active',
                        'Account Disabled',
                        'Pending',
                        'Late Applicants',
                        'Terminated'
                    ],
                    value: 'All'
                }
            }
        },
        'LSA Balance': {
            icon: 'shoppingCart',
            description: 'View the current LSA balances per member.',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' },
                class: { default: 'All Classes' }
            },
            filters: {
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions',
                    isRequired: true
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Active',
                        'Account Disabled',
                        'Pending',
                        'Late Applicants',
                        'Terminated'
                    ],
                    value: 'All'
                }
            }
        },
        'Claims Adjudication': {
            icon: 'verifiedUser',
            description: 'Pull a Claims Adjudication Report',
            hideReportTable: true,
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                type: {
                    type: 'bubble-select',
                    label: 'Date Range Filter Type',
                    options: [ 'Date Added', 'Paid Date' ],
                    value: 'Date Added'
                },
                carrier: {
                    type: 'select',
                    label: 'Carrier',
                    options: [ 'All', 'Advica', 'AIG', 'Allstate', 'ASO', 'Assumption Life', 'CHUBB', 'Desjardins', 'Empire', 'Equitable Life', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Orion', 'SSQ', 'Sunlife', 'Wawanesa' ],
                    value: 'All'
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Critical Illness',
                        'Death & Dismemberment',
                        'Dependent Life',
                        'Long Term Disability',
                        'Short Term Disability',
                        'Life Insurance',
                        'Drugs',
                        'Dental',
                        'Health',
                        'Paramedicals',
                        'Vision',
                        'Out of Country',
                        'Employee Assistance',
                        'Spending Accounts',
                        'Lifestyle Spending',
                        'Health Spending',
                        'Virtual Healthcare',
                        'Financial Awareness'
                    ],
                    value: 'All'
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Pending',
                        'Referred',
                        'Approved',
                        'Paid',
                        'Declined',
                        'Stopped'
                    ],
                    value: 'All'
                },
                className: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes'
                },
                divisionName: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        },
        'Orion': {
            icon: 'flight_takeoff',
            description: 'Pull a report of active members with OOC covered by Orion',
            filters: {}
        },
        'Benefit Carrier': {
            icon: 'handshake',
            description: 'Pull a report to review member, group and/or benefit details related to a specific carrier.',
            hideReportTable: true,
            filters: {
                carrier: {
                    type: 'select',
                    label: 'Carrier',
                    options: [ 'Advica', 'AIG', 'Allstate', 'ASO', 'Assumption Life', 'Camden', 'CHUBB', 'Desjardins', 'Dialogue', 'Empire', 'Enriched Academy', 'Equitable Life', 'Fenchurch', 'Gotodoctor', 'Humania Assurance', 'Industrial Alliance', 'Industrial Alliance Special Risk', 'Kii', 'La Capitale', 'Maple', 'Orion', 'Simply Benefits', 'SSQ', 'Sunlife', 'Wawanesa' ],
                    value: 'Empire',
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Life',
                        'Accidental Death Dismemberment',
                        'Dependent Life',
                        'Short Term Disability',
                        'Long Term Disability',
                        'Critical Illness',
                        'Drugs',
                        'Healthcare',
                        'Paramedical',
                        'Vision',
                        'Out Of Country Province',
                        'Employee Family Assistance Program',
                        'Virtual Healthcare',
                        'Financial Awareness',
                        'Dental'
                    ],
                    value: 'All',
                    isRequired: true
                },
                additionalData: {
                    type: 'bubble-list-selector',
                    label: 'Please select any additional details that you would like to be included in the report:',
                    options: [
                        'Additional Member Details',
                        'Account Details',
                        'Dependent Details',
                        'Beneficiary Details',
                        'Trustee Details'
                    ],
                    value: [],
                    isRequired: true
                }
            }
        },
        'NEM Eligibility': {
            icon: 'grading',
            description: 'View NEM eligibility per member.',
            filters: {
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions',
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Life',
                        'Accidental Death Dismemberment',
                        'Short Term Disability',
                        'Long Term Disability'
                    ],
                    value: 'All',
                    isRequired: true
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        ...Object.values(constants.memberStatusDropdownMap)
                    ],
                    value: 'All',
                    isRequired: true
                }
            }
        },
        'Paid Claim Summary': {
            icon: 'barCharts',
            description: 'Pull a report to view a summary of benefit utilization based on claims.',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment(),
                    isRequired: true
                },
                dateRangeType: {
                    type: 'bubble-select',
                    label: 'Date Range Filter Type',
                    options: [ 'Service Date', 'Paid Date' ],
                    value: 'Service Date',
                    isRequired: true
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'All',
                        'Dental',
                        'Drugs',
                        'Health',
                        'Paramedicals',
                        'Vision'
                    ],
                    value: 'All',
                    isRequired: true
                },
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions',
                    isRequired: true
                }
            }
        }
    },
    'Financial': {
        'ASO Monthly Summary': {
            icon: 'adminPanelSettings',
            description: 'View the ASO Monthly Summary',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                from: {
                    type: 'month',
                    value: firstOfMonth
                },
                to: {
                    type: 'month',
                    value: firstOfMonth
                },
                divisionName: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                benefit: {
                    type: 'select',
                    label: 'Benefit',
                    options: [
                        'Both',
                        'Health',
                        'Dental'
                    ],
                    value: 'Both'
                }
            }
        },
        'Loss Ratio': {
            icon: 'compareArrows',
            description: 'View the loss ratio report.',
            testid: 'loss-ratio',
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                divisionName: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                }
            }
        },
        'EFAP': {
            icon: 'groups',
            description: 'Pull a report for EFAP charges by carrier.',
            filters: {
                month: {
                    type: 'month',
                    value: firstOfMonth
                },
                carrier: {
                    type: 'select',
                    label: 'Carrier',
                    options: [ 'Kii', 'Advica' ],
                    value: 'Kii'
                }
            }
        },
        'Taxable Benefit': {
            icon: 'monetizationOn',
            description: 'View yearly premium for each taxable line per member.',
            testid: 'taxable-benefit',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [ 'All Divisions' ],
                    value: 'All Divisions'
                },
                status: {
                    type: 'select',
                    label: 'Status',
                    options: [
                        'All',
                        'Active',
                        'Account Disabled',
                        'Pending',
                        'Late Applicants',
                        'Terminated'
                    ],
                    value: 'All'
                }
            }
        },
        'Adjustments': {
            icon: 'currencyExchangeIcon',
            description: 'View a detailed breakdown of pending and completed billing adjustment amounts for a group or certificate.',
            testid: 'adjustments',
            filters: {
                cert: {
                    type: 'number',
                    label: 'Cert',
                    value: ''
                }
            }
        },
        'Member-Pay Invoice': {
            icon: 'receiptIcon',
            description: 'Pull a report to review member-pay invoices for the provided month',
            titleExpansionKeys: {
                monthAndYear: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            hideReportTable: true,
            filters: {
                monthAndYear: {
                    type: 'month',
                    value: firstOfMonth
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: false
                }
            }
        },
        'Division Billing': {
            icon: 'switchAccount',
            description: 'Pull a report to view a summary of the divisional charges for a month.',
            titleExpansionKeys: {
                monthAndYear: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            hideReportTable: true,
            filters: {
                monthAndYear: {
                    type: 'month',
                    value: firstOfMonth
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: false
                }
            }
        },
        'Floats': {
            icon: 'savings',
            description: 'Pull a report to view divisional float balances.',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: { 
                type: {
                    type: 'bubble-select',
                    label: 'Float Type',
                    options: [ 'Spending Account', 'ASO In Arrears' ],
                    value: 'Spending Account'
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: false
                }
            }
        },
        'Commission by Division': {
            titleExpansionKeys: {
                from: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                to: { format: (date) => dates.formatDateForDisplay(date, 'MMM YYYY') },
                groupNum: {},
                division: { default: 'All Divisions' }
            },
            icon: 'priceCheck',
            description: 'Pull a report to see the commission amounts payable for each division of a group.',
            hideReportTable: true,
            testid: 'commission-by-division',
            filters: {
                from: {
                    type: 'month',
                    value: firstOfMonth
                },
                to: {
                    type: 'month',
                    value: firstOfMonth
                },
                division: {
                    populatedBy: 'groupNum',
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: true
                }
            }
        }
    },
    'Renewal': {
        'Top Drug': {
            icon: 'medication',
            description: 'Identify drugs with the highest accumulated payout amounts.',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                class: { default: 'All Classes' }
            },
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                limit: {
                    type: 'select',
                    label: 'Limit',
                    options: [
                        'Top 10',
                        'Top 20',
                        'Top 50',
                        'Top 100'
                    ],
                    value: 'Top 10',
                    isRequired: true
                }
            }
        },
        'Top Dental Procedures': {
            icon: 'tagFaces',
            description: 'Identify dental procedures with the highest accumulated payout amounts.',
            titleExpansionKeys: {
                groupNum: { default: 'All Groups' },
                company: {},
                division: { default: 'All Divisions' }
            },
            filters: {
                dateRange: { 
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },    
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: true
                },
                limit: {
                    type: 'select',
                    label: 'Limit',
                    options: [
                        'Top 10',
                        'Top 20'
                    ],
                    value: 'Top 10',
                    isRequired: true
                }
            }
        },
        'Top Dental Providers': {
            icon: 'badgeIcon',
            description: 'Identify dentists with the highest accumulated claim amounts.',
            titleExpansionKeys: {
                dateRange: { format: (date) => [ formatDate(date.startDate), formatDate(date.endDate) ].join(' - ') },
                groupNum: { default: 'All Groups' },
                class: { default: 'All Classes' },
                division: { default: 'All Divisions' }
            },
            filters: {
                dateRange: {
                    type: 'date',
                    startDate: moment().startOf('year'),
                    endDate: moment()
                },
                class: {
                    type: 'select',
                    label: 'Class',
                    options: [ 'All Classes' ],
                    value: 'All Classes',
                    isRequired: true
                },
                division: {
                    type: 'select',
                    label: 'Division',
                    options: [],
                    value: 'All Divisions',
                    isRequired: true
                },
                limit: {
                    type: 'select',
                    label: 'Limit',
                    options: [
                        'Top 10',
                        'Top 20'
                    ],
                    value: 'Top 10',
                    isRequired: true
                }
            }
        }
    }
};

export default reportsData;